// A tab opened before a deploy still runs the old build. Its screens load lazily, so the next one
// it opens asks for the old build's hashed chunk, which the new release may no longer serve: the
// import fails and the view would show an error until a refresh. Instead, the tab reloads once and
// picks up the new build. A second failure soon after is a real outage, so it is not retried.

const STALE_IMPORT =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;

const RELOADED_AT = "amtana:new-build-reload";
/** A reload this recent that still failed means the build is not the problem: stop reloading. */
const QUIET_MS = 30_000;

type Storage = Pick<globalThis.Storage, "getItem" | "setItem">;

/** Whether an error is the browser failing to load one of the build's own chunks. */
export function isStaleBuildError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  return STALE_IMPORT.test(message);
}

/** Reloads the page for the new build, unless it already did within the last 30 s; answers whether it did. */
export function reloadForNewBuild(options: {
  readonly storage: Storage;
  readonly now: number;
  readonly reload: () => void;
}): boolean {
  const last = Number(options.storage.getItem(RELOADED_AT) ?? 0);
  if (options.now - last < QUIET_MS) return false;
  options.storage.setItem(RELOADED_AT, String(options.now));
  options.reload();
  return true;
}

function reloadHere(): boolean {
  return reloadForNewBuild({
    storage: window.sessionStorage,
    now: Date.now(),
    reload: () => window.location.reload(),
  });
}

/** Reloads instead of failing when a chunk of an older build is gone; `true` when it reloads. */
export function recoverFromStaleBuild(error: unknown): boolean {
  return isStaleBuildError(error) && reloadHere();
}

/** Vite's own signal that a preload failed: reload for the new build instead of throwing. */
export function watchForNewBuild(): void {
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadHere()) event.preventDefault();
  });
}
